import { api_fetch } from "./client"

export interface SalesWizConversation {
  id: string
  org_id: string
  /** Null for a chain chat — a conversation about the shared retail chain catalog. */
  report_id: string | null
  title?: string | null
  archived_at?: string | null
  created_at: string
  updated_at: string
}

export interface SalesWizMessage {
  id: string
  conversation_id: string
  role: "user" | "assistant"
  content: string
  created_at: string
}

export interface CreateSalesWizConversationRequest {
  /** Omit for a chain chat, which is not tied to an uploaded report. */
  report_id?: string
  force_new?: boolean
}

export interface SendSalesWizMessageRequest {
  content: string
  current_weeks?: number
  current_geography?: string
  current_brand?: string
}

export interface UpdateSalesWizConversationRequest {
  archived?: boolean
}

export interface SalesWizStreamCallbacks {
  onThinking: (message: string) => void
  onToolCall: (toolName: string) => void
  onTextDelta: (content: string) => void
  onText: (content: string) => void
  onDone: () => void
  onError: (error: string) => void
}

const API_BASE = "/sales-wiz/conversations"

export async function createSalesWizConversation(
  request: CreateSalesWizConversationRequest
): Promise<SalesWizConversation> {
  const result = await api_fetch<SalesWizConversation>(API_BASE, {
    method: "POST",
    body: request,
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to create SalesWiz conversation: ${result.error.message}`)
}

/** Omit `report_id` to list chain chats. */
export async function listSalesWizConversations(report_id?: string): Promise<SalesWizConversation[]> {
  const params = new URLSearchParams()
  if (report_id) params.append("report_id", report_id)
  const query = params.toString()
  const result = await api_fetch<SalesWizConversation[]>(query ? `${API_BASE}?${query}` : API_BASE, {
    method: "GET",
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to list SalesWiz conversations: ${result.error.message}`)
}

export async function getSalesWizMessages(conversation_id: string): Promise<SalesWizMessage[]> {
  const result = await api_fetch<SalesWizMessage[]>(`${API_BASE}/${conversation_id}/messages`, {
    method: "GET",
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to get SalesWiz messages: ${result.error.message}`)
}

export async function updateSalesWizConversation(
  conversation_id: string,
  request: UpdateSalesWizConversationRequest
): Promise<SalesWizConversation> {
  const result = await api_fetch<SalesWizConversation>(`${API_BASE}/${conversation_id}`, {
    method: "PATCH",
    body: request,
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to update SalesWiz conversation: ${result.error.message}`)
}

export function sendSalesWizMessageStream(
  conversation_id: string,
  request: SendSalesWizMessageRequest,
  callbacks: SalesWizStreamCallbacks
): AbortController {
  const abort_controller = new AbortController()

  fetch(`/api${API_BASE}/${conversation_id}/messages/stream`, {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    credentials: "same-origin",
    body: JSON.stringify(request),
    signal: abort_controller.signal,
  })
    .then(async (response: Response) => {
      if (!response.ok) {
        callbacks.onError(`Request failed: ${await response.text()}`)
        return
      }
      const reader = response.body?.getReader()
      if (!reader) {
        callbacks.onError("No response body")
        return
      }
      const decoder = new TextDecoder()
      let buffer = ""
      let done_received = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop() || ""
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue
          const data = line.slice(6).trim()
          if (!data) continue
          try {
            const event = JSON.parse(data)
            switch (event.type) {
              case "thinking":
                callbacks.onThinking(event.message)
                break
              case "tool_call":
                callbacks.onToolCall(event.tool_name)
                break
              case "text_delta":
                callbacks.onTextDelta(event.content)
                break
              case "text":
                callbacks.onText(event.content)
                break
              case "done":
                done_received = true
                callbacks.onDone()
                break
              case "error":
                callbacks.onError(event.error)
                break
            }
          } catch (error) {
            console.error("Failed to parse SalesWiz stream event:", data, error)
          }
        }
      }

      if (!done_received && !abort_controller.signal.aborted) {
        callbacks.onDone()
      }
    })
    .catch((error: Error) => {
      if (error.name !== "AbortError") {
        callbacks.onError(error.message)
      }
    })

  return abort_controller
}
